import React, { Fragment, useMemo } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import UFOSegment from '@atlaskit/react-ufo/segment';
import { ModalContextProvider } from '@atlassian/entry-points/modal-context-provider';
import { BFFConsumerEnum, BFFProvider } from '@atlassian/navigation-bff-data-provider';
import type { ObjectListFlyoutItem } from '@atlassian/navigation-common/types';
import { NavEmptyState } from '@atlassian/navigation-common/ui/nav-empty-state';
import {
	ERROR_LOCATION__GLOBAL_SIDE_NAVIGATION,
	NavErrorBoundary,
} from '@atlassian/navigation-error-boundary';

import { withSideNavigationAnalyticsContext } from '../common/analytics/context';
import type { GlobalSideNavigationProps } from '../common/types';
import { AsyncIntlProvider } from '../common/utils/locale';
import { ItemsAndShortcutsProvider } from '../controllers/items-and-shortcuts-provider';
import { OptionsProvider } from '../controllers/options-provider';
import { PeekMenuIdProviders } from '../controllers/peek-menu-id-providers';

import { GLOBAL_SIDE_NAV_UFO_INTERACTION_NAME } from './constants';
import { SideNavigationLayout } from './layout';
import { messages } from './messages';
import { LocalStore } from './side-nav/LocalStore';
import { NetworkMigrationStore } from './side-nav/NetworkMigrationStore';
import { NetworkStore } from './side-nav/NetworkStore';
import { SideNav } from './side-nav/SideNav';

export const GlobalSideNavigation: React.ComponentType<
	GlobalSideNavigationProps<ObjectListFlyoutItem>
> = withSideNavigationAnalyticsContext(RawGlobalSideNavigation);

export default RawGlobalSideNavigation; // This is only needed to support the Atlaskit docs. See https://hello.atlassian.net/wiki/spaces/navx/pages/5305993008/Platform+Guide#Atlaskit-%3CProps%3E-component-not-rendering

function RawGlobalSideNavigation(props: GlobalSideNavigationProps): React.JSX.Element {
	const {
		app,
		appLabel,
		cloudId,
		customizationStore = 'local',
		defaultMenuVisibility,
		disableMenuCustomization = false,
		forYou,
		growthItemShortcuts,
		intlMode = 'async',
		isAnonymous = false,
		isShortcutsStatic,
		items,
		networkStorageParams,
		peekMenuId,
		shortcuts,
		userId,
		...innerProps
	} = props;
	const IntlWrapper = intlMode === 'inherit' ? Fragment : AsyncIntlProvider;

	// If user is anon, disable menu customization and use local store
	const effectiveDisableMenuCustomization = disableMenuCustomization || isAnonymous;
	const effectiveCustomizationStore = isAnonymous ? 'local' : customizationStore;

	const Store = useMemo(() => {
		switch (effectiveCustomizationStore) {
			case 'local':
				return LocalStore;
			case 'network-migration':
				return NetworkMigrationStore;
			default:
				return NetworkStore;
		}
	}, [effectiveCustomizationStore]);

	const shouldUseErrorMessages = fg('navx-4866-migrate-messages-from-nav-error-boundary');

	return (
		<UFOSegment name={GLOBAL_SIDE_NAV_UFO_INTERACTION_NAME}>
			<IntlWrapper>
				<ModalContextProvider>
					<NavErrorBoundary
						errorFallbackUI={({ header, description }) => (
							<SideNavigationLayout>
								<NavEmptyState header={header} description={description} />
							</SideNavigationLayout>
						)}
						errorMessages={shouldUseErrorMessages ? messages : undefined}
						intlMode={intlMode}
						location={shouldUseErrorMessages ? undefined : ERROR_LOCATION__GLOBAL_SIDE_NAVIGATION}
						sentryConfig={{ product: app }}
						ufoInteractionName={GLOBAL_SIDE_NAV_UFO_INTERACTION_NAME}
					>
						<BFFProvider consumer={BFFConsumerEnum.GLOBAL_SIDE_NAVIGATION}>
							<OptionsProvider
								app={app}
								appLabel={appLabel}
								cloudId={cloudId}
								customizationStore={customizationStore}
								defaultMenuVisibility={defaultMenuVisibility}
								disableMenuCustomization={effectiveDisableMenuCustomization}
								hasBottomElement={!!innerProps?.bottomElem}
								intlMode={intlMode}
								isShortcutsStatic={isShortcutsStatic || isAnonymous}
								l1DragAndDropEnabled={!effectiveDisableMenuCustomization}
								networkStorageParams={networkStorageParams}
								userId={userId}
							>
								<ItemsAndShortcutsProvider
									forYou={isAnonymous ? {} : forYou}
									growthItemShortcuts={growthItemShortcuts}
									items={isAnonymous && props.anonymousItems ? props.anonymousItems : items}
									rawShortcuts={shortcuts}
								>
									<PeekMenuIdProviders value={peekMenuId}>
										<Store>
											<SideNav {...innerProps} />
										</Store>
									</PeekMenuIdProviders>
								</ItemsAndShortcutsProvider>
							</OptionsProvider>
						</BFFProvider>
					</NavErrorBoundary>
				</ModalContextProvider>
			</IntlWrapper>
		</UFOSegment>
	);
}
